/*! Copyright Disney. All rights reserved. */

import {
    HTTP_INTERCEPTORS,
    HttpClient,
    provideHttpClient,
    withInterceptorsFromDi,
} from "@angular/common/http";
import {
    ApplicationConfig,
    enableProdMode,
    ErrorHandler,
    importProvidersFrom,
    provideAppInitializer,
} from "@angular/core";

import { provideRouter, withInMemoryScrolling, withPreloading, withRouterConfig } from "@angular/router";
import { ClientIntegration } from '@wdpr/profile-authenticator-universal';
import { CookieService } from "ngx-cookie-service";
import { BrowserModule } from "@angular/platform-browser";
import { LazyPreloadingStrategy } from "@fpr/core/preloading/lazy-preloading.strategy";
import { WINDOW_PROVIDERS } from "@fpr/core/services/window.factory";
import { VasService, vasServiceCreator } from "@fpr/core/services/vas.service";
import { ConfigService } from "@fpr/core/services/config.service";
import { ErrorHandlerService } from "@fpr/core/services/error-handler.service";
import { ConversationInterceptor } from "@fpr/core/interceptors/conversation.interceptor";
import { ErrorInterceptor } from "@fpr/core/interceptors/error.interceptor";
import { TokenInterceptor } from "@fpr/core/interceptors/token.interceptor";

import routes from "./app.routes";

import { provideAnimations } from "@angular/platform-browser/animations";
import { environment } from "src/environments/environment";
import { ServiceWorkerModule } from "@angular/service-worker";
import { TranslateModule, TranslateLoader } from "@ngx-translate/core";
import { LocaleService } from "@fpr/core/services/locale.service"; 
import { TranslateHttpLoader } from "@ngx-translate/http-loader";

import "@angular/common/locales/global/pt";
import "@angular/common/locales/global/en";
import "@angular/common/locales/global/es";
import "@angular/common/locales/global/fr";

const FEATURE = 'wdpr-fastpass-reservation-ui';

export const SUPPORTED_LANGUAGES = [
    "en", // English
    "pt", // Portuguese
    "es", // Spanish
    "fr", // French
];

if (environment.production) {
    enableProdMode();
}

export const initializeAppFactory = () => {
    return async () => {
        await ClientIntegration.AngularInit.runNow({ feature: FEATURE });
    };
};

export const appConfig: ApplicationConfig = {
    providers: [
        provideRouter(
            routes,
            withPreloading(LazyPreloadingStrategy),
            withInMemoryScrolling({
                scrollPositionRestoration: "enabled",
            }),
            withRouterConfig({
                paramsInheritanceStrategy: "always",
            }),
        ),
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(
            BrowserModule,
            ServiceWorkerModule.register("scripts/ngsw-worker.js", {
                enabled: environment.production,
            }),
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: TranslateHttpLoaderFactory,
                    deps: [HttpClient, ConfigService],
                },
            }),
        ),
        provideAnimations(),
        LocaleService,
        ConfigService,
        CookieService,
        LazyPreloadingStrategy,
        WINDOW_PROVIDERS,
        {
            provide: VasService,
            useFactory: vasServiceCreator,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ConversationInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ErrorInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: ErrorHandlerService,
        },
        provideAppInitializer(() => {
            const initializerFn = initializeAppFactory();

            return initializerFn();
        })
    ]
};

export function TranslateHttpLoaderFactory(
    http: HttpClient,
    configService: ConfigService,
) {
    return new TranslateHttpLoader(
        http,
        `${configService.VAS_CONTEXT_PATH}/api/v1/content/`,
        "",
    );
}

export function SetLocalId(localeService: LocaleService) {
    return SUPPORTED_LANGUAGES.includes(localeService.lang)
        ? localeService.locale
        : "en-us";
}
